// TF-2 Phase 5 — Mediation Workflows (admin surface).
//
// One tab for the Investigation Workspace. Mirrors EvidenceTab patterns but is
// pure JSON-form (no file upload). Contains:
//   - ScheduleMediationDialog
//   - MediationCard  (5-panel notes + commitments table + compliance + verify + publish)
//   - CommitmentRow
//   - VerifyComplianceDialog
//   - PublishSummaryDialog
//   - LinkEvidenceDialog
import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import i18next from 'i18next';

// UAT #3 — ES/EN localization helper (file was English-only).
const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);
import { Handshake, CheckCircle2, AlertTriangle, XCircle, Pause, ShieldCheck, FileText, Trash2, Plus, ExternalLink, Edit3, Megaphone, Inbox, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { DepartmentMultiSelect } from '@/components/talentfix/DepartmentMultiSelect';

const OUTCOME_META = {
  resolved:           { en: 'Resolved', es: 'Resuelta',           tone: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 },
  partially_resolved: { en: 'Partially resolved', es: 'Parcialmente resuelta', tone: 'bg-amber-100 text-amber-700',     Icon: AlertTriangle },
  not_resolved:       { en: 'Not resolved', es: 'No resuelta',       tone: 'bg-rose-100 text-rose-700',       Icon: XCircle },
  escalated:          { en: 'Escalated', es: 'Escalada',          tone: 'bg-purple-100 text-purple-700',   Icon: Megaphone },
};

const STATUS_META = {
  scheduled: { en: 'Scheduled', es: 'Programada', tone: 'bg-blue-100 text-blue-700' },
  completed: { en: 'Completed', es: 'Completada', tone: 'bg-slate-100 text-slate-700' },
  cancelled: { en: 'Cancelled', es: 'Cancelada', tone: 'bg-rose-50 text-rose-600 line-through' },
  reopened:  { en: 'Reopened', es: 'Reabierta',  tone: 'bg-amber-100 text-amber-800' },
};

const COMMITMENT_TONE = {
  open:        'bg-blue-100 text-blue-700',
  done:        'bg-emerald-100 text-emerald-700',
  missed:      'bg-rose-100 text-rose-700',
  superseded:  'bg-slate-100 text-slate-500',
  blocked:     'bg-amber-100 text-amber-700',
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso).slice(0, 16).replace('T', ' '); }
}

// ============================================================================
//  Schedule dialog
// ============================================================================
function ScheduleMediationDialog({ open, onOpenChange, caseId, onCreated }) {
  const [kind, setKind] = useState('1_on_1_mediation');
  const [format, setFormat] = useState('video');
  const [dateTime, setDateTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [participantsCsv, setParticipantsCsv] = useState('');
  const [participantLabelsCsv, setParticipantLabelsCsv] = useState('');
  const [pre, setPre] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) { setKind('1_on_1_mediation'); setFormat('video'); setDateTime(''); setDuration(60); setParticipantsCsv(''); setParticipantLabelsCsv(''); setPre(''); } }, [open]);
  const submit = async () => {
    const ids = participantsCsv.split(',').map((s) => s.trim()).filter(Boolean);
    const labels = participantLabelsCsv.split(',').map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0 && labels.length === 0) { toast.error(L('At least one participant','Se requiere al menos un participante')); return; }
    setSubmitting(true);
    try {
      await adminApi.tfScheduleMediation(caseId, {
        kind, format,
        date_time: dateTime || null,
        duration_minutes: Number(duration) || null,
        participant_user_ids: ids,
        participant_external_labels: labels,
        pre_briefing_notes: pre,
      });
      toast.success(L('Mediation scheduled','Mediación programada'));
      onOpenChange(false); onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf5-mediation-schedule-dialog">
        <DialogHeader><DialogTitle>{L('Schedule mediation','Programar mediación')}</DialogTitle><DialogDescription>{L('Add a session to the case.','Agrega una sesión al caso.')}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Kind</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="tf5-schedule-kind"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="1_on_1_mediation">1-on-1 mediation</SelectItem>
                <SelectItem value="pair_mediation">{L('Pair mediation','Mediación entre dos personas')}</SelectItem>
                <SelectItem value="group_mediation">{L('Group mediation','Mediación grupal')}</SelectItem>
                <SelectItem value="cross_dept_mediation">{L('Cross-dept mediation','Mediación interdepartamental')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Format','Formato')}</label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="video">{L('Video','Video')}</SelectItem>
                <SelectItem value="in_person">{L('In person','Presencial')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium text-slate-600">{L('Date & time','Fecha y hora')}</label>
              <Input type="datetime-local" value={dateTime} onChange={(e) => setDateTime(e.target.value)} data-testid="tf5-schedule-datetime"/>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{L('Duration (minutes)','Duración (minutos)')}</label>
              <Input type="number" min={15} max={1440} value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="tf5-schedule-duration"/>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Participant user IDs (comma-separated)','IDs de participantes (separados por coma)')}</label>
            <Input value={participantsCsv} onChange={(e) => setParticipantsCsv(e.target.value)} data-testid="tf5-schedule-participant-ids"/>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Participant labels (external)','Etiquetas de participantes (externos)')}</label>
            <Input value={participantLabelsCsv} onChange={(e) => setParticipantLabelsCsv(e.target.value)} data-testid="tf5-schedule-participants"/>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">{L('Pre-briefing notes','Notas previas a la sesión')}</label>
            <Textarea rows={3} value={pre} onChange={(e) => setPre(e.target.value)} data-testid="tf5-schedule-pre"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf5-schedule-submit">{submitting ? 'Saving…' : 'Schedule'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
//  Commitment row + add inline
// ============================================================================
function CommitmentRow({ mid, c, locked, onChanged, departmentSuggestions = [] }) {
  const [editing, setEditing] = useState(false);
  const [editingDepts, setEditingDepts] = useState(false);
  const [depts, setDepts] = useState(c.department_attribution || []);
  const [status, setStatus] = useState(c.status);
  const [busy, setBusy] = useState(false);
  const save = async (newStatus) => {
    setBusy(true);
    try {
      await adminApi.tfPatchCommitment(mid, c.id, { status: newStatus });
      setStatus(newStatus);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); setEditing(false); }
  };
  const saveDepts = async () => {
    setBusy(true);
    try {
      await adminApi.tfPatchCommitment(mid, c.id, { department_attribution: depts });
      onChanged?.();
      setEditingDepts(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm('Delete this commitment?')) return;
    try {
      await adminApi.tfDeleteCommitment(mid, c.id);
      toast.success(L('Deleted','Eliminado')); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <tr data-testid={`tf5-commitment-row-${c.id}`} className="border-b text-sm">
      <td className="px-2 py-1.5">{c.description}</td>
      <td className="px-2 py-1.5 text-xs text-slate-500">{c.owner_label || c.owner_user_id || '—'}</td>
      <td className="px-2 py-1.5 text-xs text-slate-500">{c.due_date ? c.due_date.slice(0, 10) : '—'}</td>
      <td className="px-2 py-1.5 text-xs text-slate-500">
        {editingDepts ? (
          <div className="space-y-1">
            <DepartmentMultiSelect
              value={depts}
              onChange={setDepts}
              suggestions={departmentSuggestions}
              testIdPrefix={`tf5-edit-commitment-depts-${c.id}`}
            />
            <div className="flex gap-1">
              <Button size="sm" onClick={saveDepts} disabled={busy} className="h-6 text-xs"
                      data-testid={`tf5-save-commitment-depts-${c.id}`}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingDepts(false); setDepts(c.department_attribution || []); }} className="h-6 text-xs">{L('Cancel','Cancelar')}</Button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => !locked && setEditingDepts(true)}
                  disabled={locked}
                  data-testid={`tf5-commitment-depts-${c.id}`}
                  className={`text-xs ${!locked ? 'hover:underline cursor-pointer' : ''}`}>
            {(c.department_attribution || []).join(', ') || (locked ? '—' : 'Add departments…')}
          </button>
        )}
      </td>
      <td className="px-2 py-1.5">
        {editing ? (
          <Select value={status} onValueChange={save} disabled={busy}>
            <SelectTrigger className="h-7"><SelectValue/></SelectTrigger>
            <SelectContent>
              {['open', 'done', 'missed', 'blocked', 'superseded'].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <button type="button" data-testid={`tf5-commitment-status-${c.id}`}
                  className={`rounded px-2 py-0.5 text-xs ${COMMITMENT_TONE[status] || ''}`}
                  onClick={() => setEditing(true)}>{status}</button>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {!locked && (
          <Button size="sm" variant="ghost" onClick={del} className="text-rose-600 h-7 px-2"
                  data-testid={`tf5-commitment-delete-${c.id}`}>
            <Trash2 className="h-3.5 w-3.5"/>
          </Button>
        )}
      </td>
    </tr>
  );
}

function AddCommitmentInline({ mid, onChanged, departmentSuggestions = [] }) {
  const [desc, setDesc] = useState('');
  const [owner, setOwner] = useState('');
  const [due, setDue] = useState('');
  const [depts, setDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (desc.trim().length < 2) return toast.error(L('Description required','La descripción es obligatoria'));
    setBusy(true);
    try {
      await adminApi.tfAddCommitment(mid, {
        description: desc.trim(),
        owner_label: owner || null,
        due_date: due || null,
        department_attribution: depts,
      });
      setDesc(''); setOwner(''); setDue(''); setDepts([]);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 px-2 py-2 bg-slate-50">
      <div className="flex gap-2">
        <Input placeholder={L('What was committed?','¿Qué se acordó?')} value={desc} onChange={(e) => setDesc(e.target.value)} className="flex-1" data-testid={`tf5-add-commitment-desc-${mid}`}/>
        <Input placeholder={L('Owner label','Responsable')} value={owner} onChange={(e) => setOwner(e.target.value)} className="w-40"/>
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="w-40"/>
        <Button size="sm" onClick={submit} disabled={busy} data-testid={`tf5-add-commitment-btn-${mid}`}>
          <Plus className="h-3.5 w-3.5"/>
        </Button>
      </div>
      <DepartmentMultiSelect
        value={depts}
        onChange={setDepts}
        suggestions={departmentSuggestions}
        placeholder={L('Departments (optional) — Enter to add','Departamentos (opcional) — Enter para agregar')}
        testIdPrefix={`tf5-add-commitment-depts-${mid}`}
      />
    </div>
  );
}

// ============================================================================
//  Complete / Cancel / Verify / Publish dialogs
// ============================================================================

function CompleteDialog({ open, onOpenChange, mid, onDone }) {
  const [outcome, setOutcome] = useState('resolved');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setOutcome('resolved'); setNote(''); } }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.tfCompleteMediation(mid, { outcome, completion_note: note || null });
      toast.success(L('Mediation completed','Mediación completada'));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf5-complete-dialog">
        <DialogHeader><DialogTitle>{L('Complete mediation','Completar mediación')}</DialogTitle><DialogDescription>{L('Notes will be locked. A synthetic mediation_record evidence will be created automatically.','Las notas quedarán bloqueadas. Se creará automáticamente una evidencia mediation_record.')}</DialogDescription></DialogHeader>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger data-testid="tf5-complete-outcome"><SelectValue/></SelectTrigger>
          <SelectContent>
            {Object.entries(OUTCOME_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{L(m.en, m.es)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Textarea rows={2} placeholder={L('Completion note (optional)','Nota de cierre (opcional)')} value={note} onChange={(e) => setNote(e.target.value)}/>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={busy} data-testid="tf5-complete-submit">{busy ? 'Saving…' : 'Complete'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VerifyDialog({ open, onOpenChange, mid, current, onDone }) {
  const [status, setStatus] = useState(current?.verified_status || 'verified');
  const [notes, setNotes] = useState(current?.notes || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStatus(current?.verified_status || 'verified'); setNotes(current?.notes || ''); } }, [open, current]);
  const submit = async () => {
    setBusy(true);
    try {
      await adminApi.tfVerifyCompliance(mid, { verified_status: status, notes });
      toast.success(L('Verification saved','Verificación guardada'));
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf5-verify-dialog">
        <DialogHeader><DialogTitle>{L('Verify compliance','Verificar cumplimiento')}</DialogTitle><DialogDescription>{L('Record whether the committed actions actually happened. Does NOT auto-flip individual commitments.','Registra si las acciones acordadas realmente ocurrieron. NO cambia automáticamente los compromisos individuales.')}</DialogDescription></DialogHeader>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="tf5-verify-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">{L('Pending','Pendiente')}</SelectItem>
            <SelectItem value="verified">{L('Verified','Verificado')}</SelectItem>
            <SelectItem value="partially_verified">{L('Partially verified','Parcialmente verificado')}</SelectItem>
            <SelectItem value="not_verified">{L('Not verified','No verificado')}</SelectItem>
            <SelectItem value="superseded">{L('Superseded','Reemplazado')}</SelectItem>
          </SelectContent>
        </Select>
        <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={L('Verifier notes','Notas del verificador')} data-testid="tf5-verify-notes"/>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={busy} data-testid="tf5-verify-submit">{busy ? 'Saving…' : 'Save verification'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReopenDialog({ open, onOpenChange, mid, onDone }) {
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setRationale(''); }, [open]);
  const submit = async () => {
    if (rationale.trim().length < 20) {
      toast.error(L('Rationale must be at least 20 characters','La justificación debe tener al menos 20 caracteres')); return;
    }
    setBusy(true);
    try {
      await adminApi.tfReopenMediation(mid, { rationale: rationale.trim() });
      toast.success(L('Mediation reopened — you can now edit notes and re-complete.','Mediación reabierta — ya puedes editar notas y volver a completar.'));
      onOpenChange(false); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reopen');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf5-reopen-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1.5 text-amber-800">
            <RotateCcw className="h-4 w-4"/>Reopen mediation
          </DialogTitle>
          <DialogDescription>
            Returns the mediation to an editable state. The action is permanently audited
            with your rationale (20+ characters). Re-completing the mediation will update the
            existing synthetic evidence in place (no duplicate row).
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={rationale} onChange={(e) => setRationale(e.target.value)}
                  placeholder={L('Why does this mediation need to be reopened? (20+ chars — legal-reviewable)','¿Por qué debe reabrirse esta mediación? (20+ caracteres — revisable legalmente)')}
                  data-testid="tf5-reopen-rationale"/>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="tf5-reopen-submit">
            {busy ? 'Reopening…' : 'Reopen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PublishDialog({ open, onOpenChange, mid, current, onDone }) {
  const [summary, setSummary] = useState(current || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setSummary(current || ''); }, [open, current]);
  const submit = async (publish) => {
    setBusy(true);
    try {
      await adminApi.tfPublishMediationSummary(mid, { summary: publish ? summary : '' });
      toast.success(publish ? 'Summary published' : 'Summary unpublished');
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf5-publish-dialog">
        <DialogHeader><DialogTitle>{L('Publish summary to client','Publicar resumen al cliente')}</DialogTitle>
          <DialogDescription>{L('Only this summary will be visible to the Client Admin. Full notes are never shared.','Solo este resumen será visible para el Client Admin. Las notas completas nunca se comparten.')}</DialogDescription></DialogHeader>
        <Textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={2000} placeholder={L('Plain-language outcome summary the client can see','Resumen del resultado en lenguaje claro visible para el cliente')} data-testid="tf5-publish-summary-text"/>
        <DialogFooter>
          <Button variant="outline" onClick={() => submit(false)} disabled={busy} data-testid="tf5-publish-unpublish">{L('Unpublish','Despublicar')}</Button>
          <Button onClick={() => submit(true)} disabled={busy || !summary.trim()} data-testid="tf5-publish-submit">{busy ? 'Saving…' : 'Publish'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ open, onOpenChange, mid, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (reason.trim().length < 5) { toast.error(L('5+ char reason required','Motivo de mínimo 5 caracteres requerido')); return; }
    setBusy(true);
    try {
      await adminApi.tfCancelMediation(mid, { reason });
      toast.success(L('Cancelled','Cancelada')); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{L('Cancel mediation','Cancelar mediación')}</DialogTitle></DialogHeader>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={L('Reason (≥ 5 chars)','Motivo (≥ 5 caracteres)')}/>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Back</Button>
          <Button onClick={submit} disabled={busy} data-testid="tf5-cancel-submit" className="bg-rose-600 hover:bg-rose-700">{busy ? 'Saving…' : 'Cancel mediation'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkEvidenceDialog({ open, onOpenChange, mid, onDone }) {
  const [eid, setEid] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setEid(''); }, [open]);
  const submit = async () => {
    if (!eid.trim()) return;
    setBusy(true);
    try {
      await adminApi.tfLinkMediationEvidence(mid, { evidence_id: eid.trim() });
      toast.success(L('Linked','Vinculado')); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>{L('Link existing evidence','Vincular evidencia existente')}</DialogTitle>
          <DialogDescription>{L('Paste the evidence ID from the Evidence tab. Must belong to the same case.','Pega el ID de evidencia desde la pestaña Evidencia. Debe pertenecer al mismo caso.')}</DialogDescription></DialogHeader>
        <Input value={eid} onChange={(e) => setEid(e.target.value)} placeholder="evidence_id" data-testid="tf5-link-evidence-id"/>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={busy} data-testid="tf5-link-evidence-submit">{busy ? 'Linking…' : 'Link'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
//  Notes panel (5 textareas) — editable only while status=scheduled
// ============================================================================
const PANELS = [
  { key: 'opening_positions', en: 'Opening positions', es: 'Posiciones iniciales' },
  { key: 'interests_revealed', en: 'Interests revealed', es: 'Intereses revelados' },
  { key: 'points_of_agreement', en: 'Points of agreement', es: 'Puntos de acuerdo' },
  { key: 'points_of_disagreement', en: 'Points of disagreement', es: 'Puntos de desacuerdo' },
  { key: 'advisor_only_impressions', en: 'Advisor-only impressions (private)', es: 'Impresiones solo del consultor (privadas)' },
];

function NotesPanels({ mid, locked, notes, privateNotes, onSaved }) {
  const [local, setLocal] = useState(notes || {});
  const [priv, setPriv] = useState(privateNotes || { mediator_personal_notes: '', raw_advisor_impressions: '' });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setLocal(notes || {});
    setPriv(privateNotes || { mediator_personal_notes: '', raw_advisor_impressions: '' });
    setDirty(false);
  }, [notes, privateNotes, mid]);
  const save = async () => {
    setSaving(true);
    try {
      await adminApi.tfPatchMediation(mid, {
        session_notes_structured: local,
        mediator_personal_notes: priv.mediator_personal_notes || null,
        raw_advisor_impressions: priv.raw_advisor_impressions || null,
      });
      toast.success(L('Notes saved','Notas guardadas')); setDirty(false); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-2">
      {PANELS.map((p) => (
        <div key={p.key}>
          <label className="text-xs font-medium text-slate-600">{L(p.en, p.es)}</label>
          <Textarea
            rows={2}
            value={local[p.key] || ''}
            onChange={(e) => { setLocal({ ...local, [p.key]: e.target.value }); setDirty(true); }}
            disabled={locked}
            data-testid={`tf5-notes-panel-${mid}-${p.key}`}
            className={p.key === 'advisor_only_impressions' ? 'bg-amber-50' : ''}
          />
        </div>
      ))}

      {/* MVP Sprint Item 6 — advisor-only private notes (stripped from clients by Phase 7 scrubber) */}
      <div className="rounded-md border border-amber-200 bg-amber-50/40 p-2 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-amber-800 uppercase tracking-wide">{L('Advisor-only private notes','Notas privadas solo del consultor')}</p>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-200 text-amber-900 text-xs font-medium px-2 py-0.5">
            <ShieldCheck className="h-3 w-3" />Locked from client view
          </span>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{L('Mediator personal notes','Notas personales del mediador')}</label>
          <Textarea
            rows={2}
            value={priv.mediator_personal_notes || ''}
            onChange={(e) => { setPriv({ ...priv, mediator_personal_notes: e.target.value }); setDirty(true); }}
            disabled={locked}
            placeholder={L('Personal observations of the mediator — NEVER shared with client.','Observaciones personales del mediador — NUNCA se comparten con el cliente.')}
            data-testid={`tf5-mediator-personal-notes-${mid}`}
            className="bg-white"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">{L('Raw advisor impressions','Impresiones en bruto del consultor')}</label>
          <Textarea
            rows={2}
            value={priv.raw_advisor_impressions || ''}
            onChange={(e) => { setPriv({ ...priv, raw_advisor_impressions: e.target.value }); setDirty(true); }}
            disabled={locked}
            placeholder={L('Unfiltered advisor impressions — pre-synthesis. NEVER shared with client.','Impresiones sin filtrar del consultor — previas a la síntesis. NUNCA se comparten con el cliente.')}
            data-testid={`tf5-raw-advisor-impressions-${mid}`}
            className="bg-white"
          />
        </div>
      </div>

      {!locked && dirty && (
        <Button size="sm" onClick={save} disabled={saving} data-testid={`tf5-notes-save-${mid}`}>
          {saving ? 'Saving…' : 'Save notes'}
        </Button>
      )}
    </div>
  );
}

// ============================================================================
//  Mediation card  (stateless wrt mediation — parent owns the list refetch)
// ============================================================================
function MediationCard({ m, onChanged }) {
  const [open, setOpen] = useState(false);
  const [dlg, setDlg] = useState(null); // 'complete' | 'cancel' | 'verify' | 'publish' | 'link' | 'reopen'
  const oMeta = m.outcome ? OUTCOME_META[m.outcome] : null;
  const sMeta = STATUS_META[m.status];
  const isCompleted = m.status === 'completed';
  const isCancelled = m.status === 'cancelled';
  const isReopened  = m.status === 'reopened';
  // MVP Sprint Item 1 — `reopened` unlocks edits.
  const locked = isCompleted || isCancelled;
  const isPublished = !!m.client_visible_outcome_summary_published_at;
  const cc = m.commitment_compliance || { total: 0, completed: 0, missed: 0, open: 0, percentage: 0.0 };
  return (
    <Card data-testid={`tf5-mediation-card-${m.id}`} className="overflow-hidden">
      <CardHeader className="cursor-pointer pb-2" onClick={() => setOpen((o) => !o)} data-testid={`tf5-mediation-card-header-${m.id}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            {open ? <ChevronDown className="h-4 w-4 mt-1"/> : <ChevronRight className="h-4 w-4 mt-1"/>}
            <div>
              <CardTitle className="text-sm capitalize flex flex-wrap items-center gap-1.5">
                <Handshake className="h-4 w-4"/>
                {(m.kind || '').replace(/_/g, ' ')}
                <Badge className={sMeta.tone}>{L(sMeta.en, sMeta.es)}</Badge>
                {oMeta && (
                  <Badge className={oMeta.tone} data-testid={`tf5-outcome-badge-${m.id}`}>
                    <oMeta.Icon className="mr-1 h-3 w-3"/>{L(oMeta.en, oMeta.es)}
                  </Badge>
                )}
                {isPublished && (
                  <Badge className="bg-emerald-600 text-white" data-testid={`tf5-published-badge-${m.id}`}>
                    Published
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="mt-0.5 text-xs">
                {m.format} · {fmtDate(m.date_time)}{m.duration_minutes ? ` · ${m.duration_minutes} min` : ''} · consultant {m.consultant_name}
              </CardDescription>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">{L('commitments','compromisos')}</div>
            <div className="text-sm font-medium" data-testid={`tf5-compliance-pct-${m.id}`}>
              {cc.completed}/{cc.total} <span className="text-slate-400">·</span> {cc.percentage}%
            </div>
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-4 pt-0">
          {/* 5-panel notes */}
          <NotesPanels
            mid={m.id}
            locked={locked}
            notes={m.session_notes_structured}
            privateNotes={{
              mediator_personal_notes: m.mediator_personal_notes || '',
              raw_advisor_impressions: m.raw_advisor_impressions || '',
            }}
            onSaved={onChanged}/>

          {/* Commitments table */}
          <div className="rounded border">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-2 py-1 text-left">{L('Description','Descripción')}</th>
                  <th className="px-2 py-1 text-left">{L('Owner','Responsable')}</th>
                  <th className="px-2 py-1 text-left">Due</th>
                  <th className="px-2 py-1 text-left">Dept</th>
                  <th className="px-2 py-1 text-left">{L('Status','Estado')}</th>
                  <th className="px-2 py-1"/>
                </tr>
              </thead>
              <tbody>
                {(m.outcome_commitments || []).map((c) => (
                  <CommitmentRow key={c.id} mid={m.id} c={c} locked={locked} onChanged={onChanged}
                                 departmentSuggestions={m.department_scope || []}/>
                ))}
                {(m.outcome_commitments || []).length === 0 && (
                  <tr><td colSpan={6} className="px-2 py-2 text-center text-xs text-slate-400">{L('No commitments yet','Aún no hay compromisos')}</td></tr>
                )}
              </tbody>
            </table>
            {!locked && <AddCommitmentInline mid={m.id} onChanged={onChanged}
                                              departmentSuggestions={m.department_scope || []}/>}
          </div>

          {/* Linked evidence */}
          {(m.linked_evidence_live?.length > 0 || m.linked_evidence_tombstones?.length > 0) && (
            <div className="rounded border bg-slate-50 p-2">
              <div className="mb-1 text-xs font-medium text-slate-600">
                <FileText className="inline h-3 w-3 mr-1"/>Linked evidence
              </div>
              {m.linked_evidence_live?.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs" data-testid={`tf5-linked-ev-live-${e.id}`}>
                  <ExternalLink className="h-3 w-3"/>{e.title} ({e.kind})
                </div>
              ))}
              {m.linked_evidence_tombstones?.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-xs italic text-slate-400" data-testid={`tf5-linked-ev-tomb-${e.id}`}>
                  <Trash2 className="h-3 w-3"/>{e.title} — removed {e.removed_at ? fmtDate(e.removed_at) : 'time unknown'}{e.removed_reason ? ` (${e.removed_reason})` : ''}
                </div>
              ))}
            </div>
          )}

          {/* Compliance verification */}
          {isCompleted && (
            <div className="rounded border p-2">
              <div className="flex items-center justify-between">
                <div className="text-xs">
                  <ShieldCheck className="inline h-3 w-3 mr-1"/>
                  Verification:{' '}
                  <strong>{m.compliance_verification?.verified_status || 'pending'}</strong>
                  {m.compliance_verification?.verified_at && (
                    <span className="text-slate-400 ml-1">by {m.compliance_verification.verified_by_name} on {fmtDate(m.compliance_verification.verified_at)}</span>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={() => setDlg('verify')} data-testid={`tf5-verify-btn-${m.id}`}>
                  Verify
                </Button>
              </div>
              {m.compliance_verification?.notes && (
                <p className="mt-1 text-xs text-slate-500">{m.compliance_verification.notes}</p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-1.5">
            {!locked && (
              <>
                <Button size="sm" onClick={() => setDlg('complete')} data-testid={`tf5-mediation-complete-btn-${m.id}`}>
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5"/>Complete
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDlg('cancel')} className="text-rose-600" data-testid={`tf5-mediation-cancel-btn-${m.id}`}>
                  <Pause className="mr-1 h-3.5 w-3.5"/>Cancel
                </Button>
              </>
            )}
            {isCompleted && (
              <Button size="sm" variant="outline" onClick={() => setDlg('publish')} data-testid={`tf5-publish-summary-btn-${m.id}`}>
                <Megaphone className="mr-1 h-3.5 w-3.5"/>{isPublished ? 'Edit / Unpublish' : 'Publish summary'}
              </Button>
            )}
            {isCompleted && (
              <Button size="sm" variant="outline" onClick={() => setDlg('reopen')} data-testid={`tf5-mediation-reopen-btn-${m.id}`}
                      className="border-amber-300 text-amber-800 hover:bg-amber-50">
                <RotateCcw className="mr-1 h-3.5 w-3.5"/>Reopen
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setDlg('link')} data-testid={`tf5-link-evidence-btn-${m.id}`}>
              <ExternalLink className="mr-1 h-3.5 w-3.5"/>Link evidence
            </Button>
          </div>

          {/* Reopen rationale banner (when status=reopened) */}
          {isReopened && m.reopen_rationale && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs"
                 data-testid={`tf5-reopen-banner-${m.id}`}>
              <strong className="text-amber-800">{L('Reopened:','Reabierta:')}</strong>{' '}
              <span className="text-amber-900">{m.reopen_rationale}</span>
              {m.reopened_at && <span className="ml-1 text-amber-700">· {fmtDate(m.reopened_at)}</span>}
            </div>
          )}

          {/* Published summary preview */}
          {isPublished && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs">
              <strong className="text-emerald-700">{L('Client sees:','El cliente ve:')}</strong>{' '}
              <span data-testid={`tf5-published-summary-${m.id}`}>{m.client_visible_outcome_summary}</span>
            </div>
          )}
        </CardContent>
      )}

      <CompleteDialog open={dlg === 'complete'} onOpenChange={(o) => !o && setDlg(null)} mid={m.id} onDone={onChanged}/>
      <CancelDialog open={dlg === 'cancel'} onOpenChange={(o) => !o && setDlg(null)} mid={m.id} onDone={onChanged}/>
      <VerifyDialog open={dlg === 'verify'} onOpenChange={(o) => !o && setDlg(null)} mid={m.id} current={m.compliance_verification} onDone={onChanged}/>
      <PublishDialog open={dlg === 'publish'} onOpenChange={(o) => !o && setDlg(null)} mid={m.id} current={m.client_visible_outcome_summary} onDone={onChanged}/>
      <LinkEvidenceDialog open={dlg === 'link'} onOpenChange={(o) => !o && setDlg(null)} mid={m.id} onDone={onChanged}/>
      <ReopenDialog open={dlg === 'reopen'} onOpenChange={(o) => !o && setDlg(null)} mid={m.id} onDone={onChanged}/>
    </Card>
  );
}

// ============================================================================
//  Main tab
// ============================================================================
export default function MediationTab({ caseId, planStatus }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.tfListMediations(caseId);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load mediations');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [caseId]);

  const aggregate = useMemo(() => {
    let t = 0, d = 0;
    items.forEach((m) => {
      const cc = m.commitment_compliance || {};
      t += cc.total || 0; d += cc.completed || 0;
    });
    return { total: t, completed: d, percentage: t ? Math.round((d / t) * 1000) / 10 : 0.0 };
  }, [items]);

  return (
    <div className="space-y-4" data-testid="tf5-mediation-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{L('Mediations','Mediaciones')}</h3>
          <p className="text-xs text-slate-500">
            Plan and run mediation sessions. Aggregate commitment compliance:{' '}
            <span data-testid="tf5-aggregate-compliance">
              {aggregate.completed}/{aggregate.total} ({aggregate.percentage}%)
            </span>
          </p>
        </div>
        {planStatus === 'consent_granted' && (
          <Button onClick={() => setScheduleOpen(true)} data-testid="tf5-mediation-schedule-btn">
            <Plus className="mr-1 h-4 w-4"/>Schedule mediation
          </Button>
        )}
      </div>

      {loading && items.length === 0 ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center"
             data-testid="tf5-mediation-empty">
          <Inbox className="mb-2 h-8 w-8 text-slate-400"/>
          <p className="text-sm font-medium text-slate-700">{L('No mediations yet','Aún no hay mediaciones')}</p>
          <p className="mt-1 text-xs text-slate-500">Use “Schedule mediation” to start the first session.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tf5-mediation-list">
          {items.map((m) => (
            <MediationCard key={m.id} m={m} onChanged={load}/>
          ))}
        </div>
      )}

      <ScheduleMediationDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        caseId={caseId}
        onCreated={load}
      />
    </div>
  );
}
